/**
 * What the select tool draws on the overlay (docs/06-UI-SPEC.md §6, SRS C4).
 *
 * The loop or box being drawn, the box around what is chosen, and the grips for
 * resizing and turning it. All of it is in screen pixels rather than canvas units, so
 * the grips stay the same size — and stay big enough for a finger — at every zoom.
 *
 * Every colour and dash here is Layer 3: freely changeable on request.
 */

import { canvasToScreen, type Point, type Rect, type Viewport } from '@opal/shared';

import { handlesFor, HANDLE_SIZE_PX, type Handle } from './selection.js';

/** How the marks the select tool leaves are drawn. */
export interface SelectStyle {
  /** The outline colour, taken from the theme. */
  readonly line: string;
  /** The fill inside a loop or box while it is being drawn. */
  readonly wash: string;
}

const MARQUEE_DASH = [6, 4];
const SELECTION_DASH = [4, 4];

function toScreen(viewport: Viewport, point: Point): Point {
  return canvasToScreen(viewport, point);
}

/** The loop or box the hand is drawing right now. */
export function drawMarquee(
  ctx: CanvasRenderingContext2D,
  path: readonly Point[],
  viewport: Viewport,
  style: SelectStyle,
  asBox: boolean,
): void {
  const first = path[0];
  const last = path[path.length - 1];
  if (!first || !last || path.length < 2) return;

  ctx.save();
  ctx.strokeStyle = style.line;
  ctx.fillStyle = style.wash;
  ctx.lineWidth = 1.5;
  ctx.setLineDash(MARQUEE_DASH);
  ctx.beginPath();

  if (asBox) {
    const a = toScreen(viewport, first);
    const b = toScreen(viewport, last);
    ctx.rect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  } else {
    const start = toScreen(viewport, first);
    ctx.moveTo(start.x, start.y);
    for (const point of path.slice(1)) {
      const at = toScreen(viewport, point);
      ctx.lineTo(at.x, at.y);
    }
    // Closed, because that is the shape the lasso will actually test against.
    ctx.closePath();
  }

  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** One grip: a white square with an outline, so it reads on ink and on paper alike. */
function drawHandle(ctx: CanvasRenderingContext2D, at: Point, style: SelectStyle): void {
  const half = HANDLE_SIZE_PX / 2;
  ctx.beginPath();
  ctx.rect(at.x - half, at.y - half, HANDLE_SIZE_PX, HANDLE_SIZE_PX);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = style.line;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/** The grip that turns the selection: a circle, to say it is not a corner. */
function drawTurnGrip(
  ctx: CanvasRenderingContext2D,
  at: Point,
  from: Point,
  style: SelectStyle,
): void {
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(at.x, at.y);
  ctx.strokeStyle = style.line;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(at.x, at.y, HANDLE_SIZE_PX / 2, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = style.line;
  ctx.stroke();
}

/** The box around what is chosen, with its grips. */
export function drawSelectionBox(
  ctx: CanvasRenderingContext2D,
  bounds: Rect,
  viewport: Viewport,
  style: SelectStyle,
): void {
  const topLeft = toScreen(viewport, { x: bounds.x, y: bounds.y });
  const bottomRight = toScreen(viewport, {
    x: bounds.x + bounds.width,
    y: bounds.y + bounds.height,
  });

  ctx.save();
  ctx.strokeStyle = style.line;
  ctx.lineWidth = 1.5;
  ctx.setLineDash(SELECTION_DASH);
  ctx.beginPath();
  ctx.rect(
    topLeft.x,
    topLeft.y,
    bottomRight.x - topLeft.x,
    bottomRight.y - topLeft.y,
  );
  ctx.stroke();
  ctx.setLineDash([]);

  const handles: readonly Handle[] = handlesFor(bounds, viewport.zoom);
  const topMiddle = { x: (topLeft.x + bottomRight.x) / 2, y: topLeft.y };
  for (const handle of handles) {
    const at = toScreen(viewport, handle.at);
    if (handle.id === 'rotate') drawTurnGrip(ctx, at, topMiddle, style);
    else drawHandle(ctx, at, style);
  }
  ctx.restore();
}
